import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, Download, SearchCheck } from 'lucide-react';
import { Card, SectionTitle, Empty, goldBtn, ghostBtn, inputCls } from '../ui';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { api, errMsg, eur, fmtDateTime } from '../api';
import { usePortal } from '../PortalAuth';

const EMPTY = { country: '', company_name: '', reg_no: '', address: '', notes: '' };
const FIELDS = [['company_name', 'Ragione sociale *'], ['country', 'Paese *'], ['reg_no', 'Numero di registrazione / partita IVA'], ['address', 'Indirizzo o città']];
const TONE = { ricevuta: 'text-[#8A8F97] border-[#1F242C]', in_corso: 'text-[#D4AF37] border-[#D4AF37]/50', completata: 'text-[#5FC18F] border-[#2E9E6A]/50', annullata: 'text-[#E37A72] border-[#E5484D]/40' };

function Form({ price, onDone, initial }) {
  const { user, refresh } = usePortal();
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const low = price > 0 && (user?.wallet_cents ?? 0) < price;
  const submit = async (e) => {
    e.preventDefault();
    if (f.company_name.trim().length < 2 || f.country.trim().length < 2) { toast.error('Indica ragione sociale e Paese'); return; }
    if (price > 0 && !confirm) { setConfirm(true); return; }
    setBusy(true);
    try { await api.post('/foreign/investigations', f); toast.success('Richiesta inviata'); setF({ ...EMPTY, country: f.country }); setConfirm(false); onDone(); refresh(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-6 mb-8">
      <SectionTitle>Nuova investigazione</SectionTitle>
      <p className="text-sm text-[#8A8F97] mb-5 max-w-3xl">Per i Paesi dove le informazioni non sono nella banca dati: un analista Smart Info raccoglie i dati e carica il report in PDF, di solito in 5-10 giorni lavorativi. Ti avvisiamo via email.</p>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="estero-inv-form">
        {FIELDS.map(([k, l]) => (
          <label key={k} className="block text-[11px] uppercase tracking-[0.18em] text-[#7A7F87]">{l}
            <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${inputCls} mt-2 normal-case tracking-normal`} data-testid={`estero-inv-${k}`} />
          </label>
        ))}
        <label className="sm:col-span-2 block text-[11px] uppercase tracking-[0.18em] text-[#7A7F87]">Cosa ti serve sapere
          <textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={`${inputCls} w-full mt-2 p-3 !h-auto min-h-[90px] text-sm normal-case tracking-normal`} data-testid="estero-inv-notes" />
        </label>
        <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-4 border-t border-[#1F242C] pt-4">
          <div data-testid="estero-inv-price">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#7A7F87]">Prezzo unico per tutti i Paesi</p>
            <p className="text-2xl font-mono text-[#D4AF37]">{price > 0 ? eur(price) : 'Gratuita'}</p>
            {price > 0 && <p className={`text-xs ${low ? 'text-[#E37A72]' : 'text-[#8A8F97]'}`} data-testid="estero-inv-balance">{low ? `Plafond insufficiente (${eur(user?.wallet_cents || 0)}): ricaricalo prima di inviare` : `Scalati dal plafond all'invio · rimborsati se l'investigazione viene annullata`}</p>}
          </div>
          <div className="flex gap-2">
            {confirm && <Button type="button" onClick={() => setConfirm(false)} className={`${ghostBtn} h-10`} data-testid="estero-inv-cancel-confirm">Annulla</Button>}
            <Button type="submit" disabled={busy || low} className={`${goldBtn} h-10`} data-testid="estero-inv-submit">{busy ? <Loader2 size={15} className="animate-spin" /> : <><Send size={15} className="mr-2" />{confirm ? `Conferma · ${eur(price)}` : 'Richiedi investigazione'}</>}</Button>
          </div>
        </div>
      </form>
    </Card>
  );
}

function Row({ r }) {
  const download = async () => {
    try {
      const res = await api.get(`/foreign/investigations/${r.id}/pdf`, { responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(res.data); a.download = `Investigazione_${r.company_name}.pdf`; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="px-6 py-4 flex flex-wrap items-center gap-4" data-testid={`estero-inv-row-${r.id}`}>
      <div className="flex-1 min-w-[220px]">
        <p className="text-sm text-[#F2F2F2]">{r.company_name}</p>
        <p className="text-xs text-[#5A5F67] font-mono">{r.country} · {fmtDateTime(r.created_at)}{r.charged_cents ? ` · ${eur(r.charged_cents)}` : ''}{r.refunded ? ' · rimborsata' : ''}</p>
        {r.admin_note && <p className="text-xs text-[#8A8F97] mt-1">{r.admin_note}</p>}
      </div>
      <span className={`text-[11px] px-2 py-1 border rounded-sm ${TONE[r.status]}`} data-testid={`estero-inv-status-${r.id}`}>{r.status_label}</span>
      {r.has_pdf && <Button onClick={download} className={`${ghostBtn} h-9`} data-testid={`estero-inv-download-${r.id}`}><Download size={14} className="mr-2" />Scarica PDF</Button>}
    </div>
  );
}

export default function Investigations({ initial = null, price = 0 }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get('/foreign/investigations').then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <Form price={price} onDone={load} initial={initial} />
      <Card>
        <div className="px-6 pt-6"><SectionTitle>Le tue investigazioni</SectionTitle></div>
        {rows && rows.length === 0 ? <Empty icon={SearchCheck} title="Nessuna investigazione" text="Le richieste inviate compaiono qui con lo stato e il PDF quando è pronto." /> : (
          <div className="divide-y divide-[#1F242C]" data-testid="estero-inv-list">{(rows || []).map((r) => <Row key={r.id} r={r} />)}</div>
        )}
      </Card>
    </div>
  );
}
